// ── usePolymarketUsMyTrades (#1031) ──────────────────────────────
//
// My Trades' Polymarket US history (GET /api/polymarket-us/my-trades): the caller's own fills and
// positions. The identity, scope-reset and visibility rules are usePolymarketUsFills' (#1007, #1015):
// state is stamped with the identity (user, org, auth session epoch) it was fetched under, a reply is
// applied only if that identity is still live when it arrives, any scope change clears the rows at once,
// a failed read keeps the current scope's rows, and a hidden tab does not poll.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { readPolymarketUsMyTrades, type UsMyTradeFill, type UsMyTradePosition } from '@/lib/polymarketUs/myTradesClient';
import { usBrowserSession } from '@/lib/polymarketUs/browserTradingClient';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';

export type UsMyTradesStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface UsMyTradesState {
  fills: UsMyTradeFill[];
  positions: UsMyTradePosition[];
  withheld: number;
  complete: boolean;
  reasons: string[];
  status: UsMyTradesStatus;
  /** Wall time of the last validated reply in this scope, or null. */
  lastSuccessMs: number | null;
}

const IDLE: UsMyTradesState = { fills: [], positions: [], withheld: 0, complete: false, reasons: [], status: 'idle', lastSuccessMs: null };
// The history is read whole on the relay, so this polls slower than the 10s fills rail.
const DEFAULT_POLL_MS = 60_000;

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const identityNow = () => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}:${getAuthSessionEpoch()}`;
};

interface Scope { identity: string }

export function usePolymarketUsMyTrades(options: { enabled?: boolean; pollMs?: number } = {}): UsMyTradesState & { refresh: () => Promise<void> } {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;

  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const who = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const authed = useAuthStore((s) => s.status === 'authenticated' && !!s.token && !!s.user?.orgId);
  const identity = `${who}:${epoch}`;

  const [stamped, setStamped] = useState<UsMyTradesState & { identity: string }>(() => ({ ...IDLE, identity }));
  const derived: UsMyTradesState = stamped.identity === identity ? stamped : IDLE;
  const current: UsMyTradesState = derived.status === 'idle' && enabled && authed ? { ...derived, status: 'loading' } : derived;

  const stateRef = useRef(stamped);
  const scopeRef = useRef<Scope | null>(null);
  const inFlightRef = useRef<Scope | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const session = usBrowserSession();
    if (!session) return;
    if (scopeRef.current?.identity !== identity) scopeRef.current = { identity };
    const scope = scopeRef.current;
    if (inFlightRef.current === scope) return;
    inFlightRef.current = scope;
    const carry = (): UsMyTradesState => (stateRef.current.identity === identity ? stateRef.current : IDLE);
    const apply = (next: UsMyTradesState) => {
      if (!mountedRef.current || scopeRef.current !== scope || identityNow() !== identity) return;
      const resolved = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
    };
    if (carry().status === 'idle') apply({ ...carry(), status: 'loading' });
    try {
      const out = await readPolymarketUsMyTrades(session);
      if (out.ok) {
        apply({ fills: out.fills, positions: out.positions, withheld: out.withheld, complete: out.complete,
          reasons: out.reasons, status: 'ready', lastSuccessMs: Date.now() });
      } else {
        // A failed read is not an empty history: keep this scope's rows.
        apply({ ...carry(), status: out.code === 'US_MY_TRADES_MALFORMED' ? 'error' : 'unavailable' });
      }
    } finally {
      if (inFlightRef.current === scope) inFlightRef.current = null;
    }
  }, [identity]);

  useEffect(() => {
    if (!enabled || !authed) return;
    let stopped = false;
    let lastPollMs = Number.NEGATIVE_INFINITY;
    const poll = () => {
      if (stopped || document.hidden) return;
      lastPollMs = performance.now();
      void refresh();
    };
    const onVisibility = () => {
      if (performance.now() - lastPollMs >= pollMs) poll(); // monotonic: an elapsed check, not a timestamp
    };
    poll();
    const intervalId = window.setInterval(poll, pollMs);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, authed, pollMs, refresh]);

  return { ...current, refresh };
}
